import React, { useState } from 'react';
import { Plus, Code, Trash2, Pencil, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { CodeEditor } from './CodeEditor';
import { Badge } from '../ui/Badge';

type Language = 'typescript' | 'python' | 'curl' | 'javascript' | 'java' | 'csharp' | 'go' | 'php';

interface CodeExample {
  id: string;
  language: Language;
  title: string;
  description: string;
  code: string;
  operation?: string;
}

interface CodeExamplesTabProps {
  codeExamples: CodeExample[];
  operations?: string[];
  onChange: (examples: CodeExample[]) => void;
  isDarkMode?: boolean;
}

const LANGUAGE_COLORS: Record<Language, string> = {
  typescript: 'bg-blue-100 text-blue-800',
  javascript: 'bg-yellow-100 text-yellow-800',
  python: 'bg-green-100 text-green-800',
  curl: 'bg-gray-100 text-gray-800',
  java: 'bg-red-100 text-red-800',
  csharp: 'bg-purple-100 text-purple-800',
  go: 'bg-cyan-100 text-cyan-800',
  php: 'bg-indigo-100 text-indigo-800',
};

export function CodeExamplesTab({
  codeExamples,
  operations = [],
  onChange,
  isDarkMode = false,
}: CodeExamplesTabProps) {
  // Theme-aware colors (used for language badges)
  const LANGUAGE_COLORS_THEMED: Record<Language, string> = isDarkMode ? {
    typescript: 'bg-blue-900/50 text-blue-300',
    javascript: 'bg-yellow-900/50 text-yellow-300',
    python: 'bg-green-900/50 text-green-300',
    curl: 'bg-gray-700 text-gray-300',
    java: 'bg-red-900/50 text-red-300',
    csharp: 'bg-purple-900/50 text-purple-300',
    go: 'bg-cyan-900/50 text-cyan-300',
    php: 'bg-indigo-900/50 text-indigo-300',
  } : LANGUAGE_COLORS;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [expandedExamples, setExpandedExamples] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState<CodeExample>({
    id: '',
    language: 'typescript',
    title: '',
    description: '',
    code: '',
    operation: undefined,
  });

  // Group examples by operation
  const groupedExamples = codeExamples.reduce((acc, example) => {
    const key = example.operation || 'Geral';
    if (!acc[key]) acc[key] = [];
    acc[key].push(example);
    return acc;
  }, {} as Record<string, CodeExample[]>);

  const openAddModal = (operation?: string) => {
    setFormData({
      id: crypto.randomUUID(),
      language: 'typescript',
      title: '',
      description: '',
      code: '',
      operation,
    });
    setEditingIndex(null);
    setIsModalOpen(true);
  };

  const openEditModal = (index: number) => {
    setFormData({ ...codeExamples[index] });
    setEditingIndex(index);
    setIsModalOpen(true);
  };

  const handleSave = () => {
    if (!formData.title || !formData.code) {
      return;
    }

    const updated = [...codeExamples];
    if (editingIndex !== null) {
      updated[editingIndex] = formData;
    } else {
      updated.push(formData);
    }
    
    onChange(updated);
    setIsModalOpen(false);
  };

  const handleDelete = (index: number) => {
    onChange(codeExamples.filter((_, i) => i !== index));
  };

  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedExamples);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedExamples(newExpanded);
  };

  const getExampleIndex = (example: CodeExample) => {
    return codeExamples.findIndex(e => e.id === example.id);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Code className="w-5 h-5" />
            Exemplos de Código
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Adicione exemplos de código em diferentes linguagens para cada operação.
          </p>
        </div>
        <Button onClick={() => openAddModal()} size="sm">
          <Plus className="w-4 h-4 mr-1" />
          Adicionar Exemplo
        </Button>
      </div>

      {/* Examples List */}
      {codeExamples.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Code className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 mb-2">Nenhum exemplo de código cadastrado</p>
          <p className="text-sm text-gray-400 mb-4">
            Adicione exemplos em TypeScript, Python, cURL e outras linguagens.
          </p>
          <Button variant="secondary" onClick={() => openAddModal()}>
            <Plus className="w-4 h-4 mr-1" />
            Adicionar primeiro exemplo
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedExamples).map(([operation, examples]) => (
            <div key={operation} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Operation Header */}
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h4 className="font-medium text-gray-900">
                  {operation === 'Geral' ? '📦 Exemplos Gerais' : `🔧 ${operation}`}
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openAddModal(operation === 'Geral' ? undefined : operation)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>

              {/* Examples */}
              <div className="divide-y divide-gray-200">
                {examples.map((example) => {
                  const isExpanded = expandedExamples.has(example.id);
                  const index = getExampleIndex(example);

                  return (
                    <div key={example.id} className="bg-white">
                      {/* Example Header */}
                      <div
                        className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50"
                        onClick={() => toggleExpanded(example.id)}
                      >
                        <button type="button" className="text-gray-400">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5" />
                          ) : (
                            <ChevronRight className="w-5 h-5" />
                          )}
                        </button>

                        <Badge className={LANGUAGE_COLORS[example.language]}>
                          {example.language}
                        </Badge>

                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{example.title}</p>
                          <p className="text-sm text-gray-500 truncate">{example.description}</p>
                        </div>

                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => openEditModal(index)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                            title="Editar"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(index)}
                            className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                            title="Remover"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Expanded Code */}
                      {isExpanded && (
                        <div className="px-4 pb-4">
                          <CodeEditor
                            value={example.code}
                            onChange={() => {}}
                            language={example.language}
                            readOnly
                            showLanguageSelector={false}
                            minHeight={150}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick Add by Operation */}
      {operations.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Adicionar exemplo para operação:
          </h4>
          <div className="flex flex-wrap gap-2">
            {operations.map((op) => (
              <Button
                key={op}
                variant="secondary"
                size="sm"
                onClick={() => openAddModal(op)}
              >
                <Plus className="w-3 h-3 mr-1" />
                {op}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingIndex !== null ? 'Editar Exemplo' : 'Novo Exemplo de Código'}
        size="lg"
      >
        <div className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título *
            </label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Ex: Criar cobrança PIX"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição
            </label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Breve descrição do que o exemplo faz"
            />
          </div>

          {/* Operation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Operação Relacionada
            </label>
            <select
              value={formData.operation || ''}
              onChange={(e) => setFormData({ ...formData, operation: e.target.value || undefined })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Geral (sem operação específica)</option>
              {operations.map((op) => (
                <option key={op} value={op}>{op}</option>
              ))}
            </select>
          </div>

          {/* Code Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Código *
            </label>
            <CodeEditor
              value={formData.code}
              onChange={(code) => setFormData({ ...formData, code })}
              language={formData.language}
              onLanguageChange={(language) => setFormData({ ...formData, language })}
              minHeight={250}
              placeholder="// Cole ou digite seu código aqui..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={!formData.title || !formData.code}>
              {editingIndex !== null ? 'Salvar' : 'Adicionar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default CodeExamplesTab;
